import { useState, useEffect, useCallback } from 'react';
import { scheduleApi } from '../services/api';

// Convert cron expression to frequency and days (cron can't specify start time)
function cronToFormFields(cron) {
  if (!cron) return { frequency: 'daily', days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] };
  
  const parts = cron.split(' ');
  if (parts.length !== 5) return { frequency: 'custom', days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] };
  
  const [minute, hour, , , dayOfWeek] = parts;
  
  
  // Convert day numbers to day names
  const dayMap = { '0': 'Sunday', '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday', '5': 'Friday', '6': 'Saturday' };
  const days = dayOfWeek.split(',').map(day => dayMap[day] || 'Monday');
  
  // Determine frequency from cron pattern
  let frequency = 'daily';
  
  // Check for minute-based patterns first (like */15, */30)
  if (minute.includes('*/15')) {
    frequency = '*/15';
  } else if (minute.includes('*/30')) {
    frequency = '*/30';
  } else if (hour === '*') {
    // Hourly patterns
    frequency = 'hourly';
  } else {
    // Daily pattern (specific hour)
    frequency = 'daily';
  }
  
  
  return { frequency, days };
}

// Convert form fields to cron expression
function formFieldsToCron(frequency, startTime, days) {
  const [hour, minute] = startTime.split(':').map(Number);
  const dayNumbers = days.map(day => {
    const dayMap = { 'Sunday': '0', 'Monday': '1', 'Tuesday': '2', 'Wednesday': '3', 'Thursday': '4', 'Friday': '5', 'Saturday': '6' };
    return dayMap[day] || '1';
  }).sort().join(',');
  
  if (frequency === '*/15') return `${minute} */15 * * ${dayNumbers}`;
  if (frequency === '*/30') return `${minute} */30 * * ${dayNumbers}`;
  if (frequency === 'hourly') return `${minute} * * * ${dayNumbers}`;
  if (frequency === 'daily') return `${minute} ${hour} * * ${dayNumbers}`;
  if (frequency === 'custom') return ''; // Handle custom cron separately
  
  return `${minute} ${hour} * * ${dayNumbers}`;
}

export function useSchedule(serviceName) {
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simple function to fetch schedule
  const fetchSchedule = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await scheduleApi.getSchedule(serviceName);
      
      if (response.success && response.data && response.data.active && response.data.cron) {
        
        // Use stored human data directly instead of parsing cron
        const storedFrequency = response.data.human?.frequency || 'daily';
        const storedDays = response.data.human?.days || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        
        
        // Transform backend data to match frontend expectations
        const transformedSchedule = {
          ...response.data,
          cronExpression: response.data.cron,  // Backend: cron -> Frontend: cronExpression
          metadata: {                          // Backend: human -> Frontend: metadata
            time: response.data.human?.startTime,           // Use stored start time
            startTime: response.data.human?.startTime,      // Use stored start time
            endTime: response.data.human?.endTime,          // Include end time
            days: storedDays,                               // Use stored days directly
            frequency: storedFrequency,                     // Use stored frequency directly
            retryOnFailure: response.data.human?.retryOnFailure !== undefined ? response.data.human.retryOnFailure : true,
            maxRetries: response.data.human?.maxRetries !== undefined ? response.data.human.maxRetries : 3
          }
        };
        
        setSchedule(transformedSchedule);
      } else {
        setSchedule(null);
      }
    } catch (err) {
      setError(err.message);
      setSchedule(null);
    } finally {
      setIsLoading(false);
    }
  }, [serviceName]);

  // Fetch on mount
  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Update schedule
  const updateSchedule = useCallback(async (scheduleData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await scheduleApi.updateSchedule(serviceName, scheduleData);
      
      if (response.success) {
        // Fetch the updated schedule
        await fetchSchedule();
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [serviceName, fetchSchedule]);

  // Remove schedule
  const removeSchedule = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await scheduleApi.removeSchedule(serviceName);
      
      if (response.success) {
        // Fetch again to confirm it's gone
        await fetchSchedule();
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [serviceName, fetchSchedule]);

  // Toggle schedule enabled/disabled
  const toggleScheduleEnabled = useCallback(async (enabled) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await scheduleApi.setScheduleEnabled(serviceName, enabled);
      
      if (response.success) {
        // Fetch again to get updated state
        await fetchSchedule();
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [serviceName, fetchSchedule]);

  return {
    schedule,
    isLoading,
    error,
    updateSchedule,
    removeSchedule,
    toggleScheduleEnabled,
    refreshSchedule: fetchSchedule,
  };
}
